/**
 * OutreachSettingsModal - Settings modal for Outreach with platform tabs
 */

import { outreachApi, type OutreachConfig, type PlatformSettings, type AIProviderSettings } from '../services/outreach-api';

// Platform definitions
const PLATFORMS = [
  { id: 'default', name: 'Default', icon: 'tune' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'linkedin' },
  { id: 'twitter', name: 'X', icon: 'twitter' },
  { id: 'instagram', name: 'Instagram', icon: 'instagram' },
  { id: 'youtube', name: 'YouTube', icon: 'youtube' }
];

// Platform SVG icons
const PLATFORM_ICONS: Record<string, string> = {
  tune: '<span class="material-symbols-outlined" style="font-size: 18px;">tune</span>',
  linkedin: '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>',
  twitter: '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  instagram: '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>',
  youtube: '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>'
};

export class OutreachSettingsModal {
  private modalEl: HTMLElement | null = null;
  private config: OutreachConfig | null = null;
  private activeTab: string = 'default';
  private onSaveCallback: (() => void) | null = null;

  constructor() {}

  async open(): Promise<void> {
    await this.loadConfig();
    this.render();
    this.setupEventListeners();
  }

  onSave(callback: () => void): void {
    this.onSaveCallback = callback;
  }

  private async loadConfig(): Promise<void> {
    try {
      this.config = await outreachApi.getConfig();
    } catch (error) {
      console.error('Failed to load outreach config:', error);
      // Default config
      this.config = {
        defaultPrompt: '',
        platforms: {
          linkedin: { enabled: true },
          twitter: { enabled: true },
          instagram: { enabled: true },
          youtube: { enabled: true }
        }
      };
    }
  }

  private render(): void {
    this.modalEl?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'outreach-settings-overlay';
    overlay.innerHTML = `
      <div class="outreach-settings-modal">
        <!-- Header -->
        <div class="outreach-settings-header">
          <h2>Outreach Settings</h2>
          <button class="outreach-settings-close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <!-- Platform Tabs -->
        <div class="outreach-settings-tabs">
          ${PLATFORMS.map(p => `
            <button class="outreach-settings-tab ${this.activeTab === p.id ? 'active' : ''}" data-tab="${p.id}">
              ${PLATFORM_ICONS[p.icon]}
              <span>${p.name}</span>
            </button>
          `).join('')}
        </div>

        <!-- Tab Content -->
        <div class="outreach-settings-content">
          ${this.renderTabContent()}
        </div>

        <!-- Footer -->
        <div class="outreach-settings-footer">
          <button class="outreach-settings-reset">Reset to Defaults</button>
          <button class="outreach-settings-save">Save Configuration</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    this.modalEl = overlay;
  }

  private renderTabContent(): string {
    if (!this.config) return '';

    const isDefault = this.activeTab === 'default';

    // Get current values
    let prompt = '';
    let promptId = '';
    let maxPerExecution = '';
    let intervalMinutes = '';
    let maxPerDay = '';
    let enabled = true;
    let aiProvider: 'openai' | 'cloudflare' = 'cloudflare';
    let aiModel = '';

    if (isDefault) {
      prompt = this.config.defaultPrompt || '';
      promptId = this.config.defaultPromptId || '';
      maxPerExecution = this.config.defaultMaxPerExecution?.toString() || '';
      intervalMinutes = this.config.defaultIntervalMinutes?.toString() || '';
      maxPerDay = this.config.defaultMaxPerDay?.toString() || '';
      aiProvider = this.config.defaultAIProvider?.provider || 'cloudflare';
      aiModel = this.config.defaultAIProvider?.model || '';
    } else {
      const platformSettings = this.config.platforms[this.activeTab] || {};
      prompt = platformSettings.prompt || '';
      promptId = platformSettings.promptId || '';
      maxPerExecution = platformSettings.maxPerExecution?.toString() || '';
      intervalMinutes = platformSettings.intervalMinutes?.toString() || '';
      maxPerDay = platformSettings.maxPerDay?.toString() || '';
      enabled = platformSettings.enabled !== false;
      aiProvider = platformSettings.aiProvider?.provider || 'cloudflare';
      aiModel = platformSettings.aiProvider?.model || '';
    }

    return `
      <!-- Prompt Section -->
      <div class="outreach-settings-field">
        <div class="outreach-settings-label">
          <label>Generation Prompt</label>
          <div class="outreach-settings-tooltip">
            <span class="material-symbols-outlined">info</span>
            <div class="outreach-settings-tooltip-content">
              <div class="tooltip-title">Available Variables:</div>
              <ul>
                <li><code>{{platform}}</code> - Target platform</li>
                <li><code>{{postAuthor}}</code> - Author name</li>
                <li><code>{{postContent}}</code> - Post body</li>
                <li><code>{{postTitle}}</code> - Post title</li>
                <li><code>{{postUrl}}</code> - Post URL</li>
              </ul>
            </div>
          </div>
        </div>
        <textarea
          id="settings-prompt"
          placeholder="${isDefault ? 'Generate a thoughtful comment for this {{platform}} post...' : 'Leave empty to use default prompt'}"
        >${this.escapeHtml(prompt)}</textarea>
      </div>

      <!-- Prompt ID -->
      <div class="outreach-settings-field">
        <label>Prompt ID <span class="optional">(for tracking)</span></label>
        <input
          type="text"
          id="settings-promptId"
          value="${this.escapeHtml(promptId)}"
          placeholder="${isDefault ? 'e.g. default-v1' : 'e.g. ' + this.activeTab + '-v1'}"/>
      </div>

      <!-- AI Provider Settings -->
      <div class="outreach-settings-grid" style="grid-template-columns: 1fr 1fr; margin-bottom: 20px;">
        <div class="outreach-settings-field">
          <label>AI Provider</label>
          <select id="settings-aiProvider" class="outreach-settings-select">
            <option value="cloudflare" ${aiProvider === 'cloudflare' ? 'selected' : ''}>Cloudflare Workers AI (Free)</option>
            <option value="openai" ${aiProvider === 'openai' ? 'selected' : ''}>OpenAI (Paid)</option>
          </select>
        </div>

        <div class="outreach-settings-field">
          <label>Model</label>
          <select id="settings-aiModel" class="outreach-settings-select">
            ${aiProvider === 'openai' ? `
              <option value="gpt-5-nano" ${aiModel === 'gpt-5-nano' || !aiModel ? 'selected' : ''}>GPT-5 Nano ($0.05/1M in)</option>
              <option value="gpt-5-mini" ${aiModel === 'gpt-5-mini' ? 'selected' : ''}>GPT-5 Mini ($0.25/1M in)</option>
              <option value="gpt-5" ${aiModel === 'gpt-5' ? 'selected' : ''}>GPT-5 ($1.25/1M in)</option>
              <option value="gpt-4o-mini" ${aiModel === 'gpt-4o-mini' ? 'selected' : ''}>GPT-4o Mini ($0.15/1M in)</option>
              <option value="gpt-4o" ${aiModel === 'gpt-4o' ? 'selected' : ''}>GPT-4o ($2.50/1M in)</option>
            ` : `
              <option value="@cf/meta/llama-3.1-8b-instruct" ${aiModel === '@cf/meta/llama-3.1-8b-instruct' || !aiModel ? 'selected' : ''}>Llama 3.1 8B</option>
              <option value="@cf/meta/llama-3.1-70b-instruct" ${aiModel === '@cf/meta/llama-3.1-70b-instruct' ? 'selected' : ''}>Llama 3.1 70B</option>
              <option value="@cf/mistral/mistral-7b-instruct-v0.1" ${aiModel === '@cf/mistral/mistral-7b-instruct-v0.1' ? 'selected' : ''}>Mistral 7B</option>
            `}
          </select>
        </div>
      </div>

      <!-- Execution Settings Grid -->
      <div class="outreach-settings-grid">
        <div class="outreach-settings-field">
          <label>Max / Execution</label>
          <input
            type="number"
            id="settings-maxPerExecution"
            value="${maxPerExecution}"
            placeholder="${isDefault ? '10' : 'Inherit'}"/>
        </div>

        <div class="outreach-settings-field">
          <label>Interval <span class="optional">(min)</span></label>
          <input
            type="number"
            id="settings-intervalMinutes"
            value="${intervalMinutes}"
            placeholder="${isDefault ? '30' : 'Inherit'}"/>
        </div>

        <div class="outreach-settings-field">
          <label>Max / Day</label>
          <input
            type="number"
            id="settings-maxPerDay"
            value="${maxPerDay}"
            placeholder="${isDefault ? '50' : 'Inherit'}"/>
        </div>
      </div>

      ${!isDefault ? `
        <!-- Platform Toggle -->
        <div class="outreach-settings-toggle-row">
          <div class="toggle-info">
            <div class="toggle-status ${enabled ? 'enabled' : 'disabled'}"></div>
            <span>Platform ${enabled ? 'enabled' : 'disabled'}</span>
          </div>
          <label class="toggle-switch">
            <input type="checkbox" id="settings-enabled" ${enabled ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
      ` : ''}
    `;
  }

  private setupEventListeners(): void {
    if (!this.modalEl) return;

    // Close button
    this.modalEl.querySelector('.outreach-settings-close')?.addEventListener('click', () => {
      this.close();
    });

    // Overlay click
    this.modalEl.addEventListener('click', (e) => {
      if (e.target === this.modalEl) this.close();
    });

    // Tab switching
    this.modalEl.querySelectorAll('.outreach-settings-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        this.saveCurrentTab();
        this.activeTab = tab.getAttribute('data-tab') || 'default';
        this.updateTabs();
        this.updateContent();
      });
    });

    // Save button
    this.modalEl.querySelector('.outreach-settings-save')?.addEventListener('click', () => {
      this.saveCurrentTab();
      this.saveConfig();
    });

    // Reset button
    this.modalEl.querySelector('.outreach-settings-reset')?.addEventListener('click', () => {
      if (confirm('Reset all settings to defaults?')) {
        this.resetToDefaults();
      }
    });

    // Escape key
    document.addEventListener('keydown', this.handleKeydown);
  }

  private handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.close();
    }
  };

  private updateTabs(): void {
    this.modalEl?.querySelectorAll('.outreach-settings-tab').forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === this.activeTab);
    });
  }

  private updateContent(): void {
    const content = this.modalEl?.querySelector('.outreach-settings-content');
    if (content) {
      content.innerHTML = this.renderTabContent();
    }
  }

  private saveCurrentTab(): void {
    if (!this.config || !this.modalEl) return;

    const prompt = (this.modalEl.querySelector('#settings-prompt') as HTMLTextAreaElement)?.value || '';
    const promptId = (this.modalEl.querySelector('#settings-promptId') as HTMLInputElement)?.value || '';
    const maxPerExecution = parseInt((this.modalEl.querySelector('#settings-maxPerExecution') as HTMLInputElement)?.value) || undefined;
    const intervalMinutes = parseInt((this.modalEl.querySelector('#settings-intervalMinutes') as HTMLInputElement)?.value) || undefined;
    const maxPerDay = parseInt((this.modalEl.querySelector('#settings-maxPerDay') as HTMLInputElement)?.value) || undefined;
    const enabledCheckbox = this.modalEl.querySelector('#settings-enabled') as HTMLInputElement;
    const aiProviderSelect = this.modalEl.querySelector('#settings-aiProvider') as HTMLSelectElement;
    const aiModelSelect = this.modalEl.querySelector('#settings-aiModel') as HTMLSelectElement;

    const aiProvider = aiProviderSelect?.value as 'openai' | 'cloudflare' || 'cloudflare';
    const aiModel = aiModelSelect?.value || '';

    if (this.activeTab === 'default') {
      if (prompt) this.config.defaultPrompt = prompt;
      if (promptId) this.config.defaultPromptId = promptId;
      this.config.defaultMaxPerExecution = maxPerExecution;
      this.config.defaultIntervalMinutes = intervalMinutes;
      this.config.defaultMaxPerDay = maxPerDay;
      this.config.defaultAIProvider = {
        provider: aiProvider,
        model: aiModel || undefined
      };
    } else {
      if (!this.config.platforms[this.activeTab]) {
        this.config.platforms[this.activeTab] = {};
      }
      const settings = this.config.platforms[this.activeTab];
      settings.prompt = prompt || undefined;
      settings.promptId = promptId || undefined;
      settings.maxPerExecution = maxPerExecution;
      settings.intervalMinutes = intervalMinutes;
      settings.maxPerDay = maxPerDay;
      if (enabledCheckbox) {
        settings.enabled = enabledCheckbox.checked;
      }
      // Only save AI provider if different from default
      if (aiProvider || aiModel) {
        settings.aiProvider = {
          provider: aiProvider,
          model: aiModel || undefined
        };
      }
    }
  }

  private async saveConfig(): Promise<void> {
    if (!this.config) return;

    try {
      await outreachApi.saveConfig(this.config);
      this.showNotification('Settings saved');
      if (this.onSaveCallback) {
        this.onSaveCallback();
      }
      this.close();
    } catch (error) {
      console.error('Failed to save config:', error);
      this.showNotification('Failed to save settings');
    }
  }

  private async resetToDefaults(): Promise<void> {
    this.config = {
      defaultPrompt: `Generate a thoughtful comment for this {{platform}} post.

Post by {{postAuthor}}:
{{postContent}}

Requirements:
- Be authentic and add value
- Max 280 characters
- Ask a follow-up question OR share a brief insight
- Professional but friendly tone
- Don't be generic or salesy

Return only the comment text, nothing else.`,
      platforms: {
        linkedin: { enabled: true },
        twitter: { enabled: true },
        instagram: { enabled: true },
        youtube: { enabled: true }
      }
    };
    this.updateContent();
  }

  private showNotification(message: string): void {
    const notification = document.createElement('div');
    notification.className = 'outreach-notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  close(): void {
    document.removeEventListener('keydown', this.handleKeydown);
    this.modalEl?.remove();
    this.modalEl = null;
  }
}
